import React from 'react';

import {
  Collapse,
  CollapseProps,
  Flex,
  Popconfirm,
  Space,
  Table,
  Tooltip,
} from 'antd-v5';
import _ from 'lodash';
import { MdDeleteOutline } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Text } from 'scouting-ui/lib';

import { useMBDict } from '@dict/duck/hooks';
import { useRemoveMBCYouthMBMutation } from '@modules/meritBadgeCounselors/api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { AdvancementStatusTag } from '@shared/components/AdvancementStatusTag';
import { advancementTypes } from '@shared/constants';
import { goToYouthAdvRequirements } from '@shared/duck/actions';
import { isMobileSel } from '@shared/duck/selectors/general.selectors';
import { getPreferredName } from '@shared/utils/preferredName';
import { toastService } from '@toasts';

import {
  dashboardSortByNameSel,
  mbcProfileGuidSel,
  mbcProfileMIDSel,
  mbcYouthsByMBSel,
  selectedYouthByMBSel,
} from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import { compareByName } from '../duck/utils';
import styles from './DashboardTabYouth.module.scss';
import MBImage from './MBImage';
import RecordProgressButton from './RecordProgressButton';
import RemoveMBModal from './RemoveMBModal';

interface MBCYouthsByMBViewProps {
  onRemove: () => void;
}

export default function MBCYouthsByMBView(props: MBCYouthsByMBViewProps) {
  const isMobile = useSelector(isMobileSel);
  const groupedByMB = useSelector(mbcYouthsByMBSel);
  const { meritBadgesById } = useMBDict();
  const dispatch = useDispatch();
  const selectedYouthByMB = useSelector(selectedYouthByMBSel);
  const sortByName = useSelector(dashboardSortByNameSel);
  const counselorMemberId = useSelector(mbcProfileMIDSel);
  const mbcPersonGuid = useSelector(mbcProfileGuidSel);
  const selectedRowKeys = new Set(selectedYouthByMB);

  const [removeCounselor, { isLoading: isRemoving }] =
    useRemoveMBCYouthMBMutation();

  const items: CollapseProps['items'] = _.chain(groupedByMB)
    .keys()
    .map(mbId => {
      const youths = groupedByMB[mbId];
      const mb = youths[0].mb;
      const mbImage = meritBadgesById.get(mb.meritBadgeId)?.imageUrl50;
      const selectedYouth = youths
        .filter(item => selectedRowKeys.has(item.rowKey))
        .map(item => item.youth);

      return {
        key: mbId,
        label: (
          <Flex gap="small">
            <Space align="center">
              <MBImage src={mbImage} width={32} height={32} />
              <Text strong color="color-text-dark" font="body-overline">
                {mb.meritBadgeName} ({youths.length})
              </Text>
            </Space>

            <RecordProgressButton
              meritBadgeId={mb.meritBadgeId}
              selectedYouth={selectedYouth}
              noSelectionMessageId="mbcDashboard.mbTable.recordProgress.noSelection"
            />
          </Flex>
        ),
        title: mb.meritBadgeName,
        children: (
          <Table
            key={sortByName}
            dataSource={youths}
            pagination={false}
            rowKey={'rowKey'}
            scroll={{ x: true }}
            rowSelection={{
              selectedRowKeys: selectedYouthByMB,
              onChange(selectedRowKeys: (string | number)[]) {
                dispatch(
                  mbcDashboardActions.setSelectedYouthByMB({
                    selectedRowKeys: selectedRowKeys as string[],
                    meritBadgeId: mb.meritBadgeId,
                  }),
                );
              },
            }}
            columns={[
              {
                title: (
                  <FormattedMessage id="mbcDashboard.youthTable.column.name" />
                ),
                key: 'name',
                fixed: 'left',
                dataIndex: 'youth.name',
                defaultSortOrder: 'ascend',
                sorter: (a, b) => compareByName(a.youth, b.youth, sortByName),
                render: (_value, record) => (
                  <Text className={styles.text}>
                    {getPreferredName(record.youth)}
                  </Text>
                ),
              },
              {
                title: (
                  <FormattedMessage id="mbcDashboard.youthTable.column.mid" />
                ),
                minWidth: 120,
                sorter: (a, b) => a.youth.memberId - b.youth.memberId,
                dataIndex: 'youth.memberId',
                render: (_value, record) => (
                  <Text className={styles.text}>{record.youth.memberId}</Text>
                ),
              },
              {
                title: (
                  <FormattedMessage id="mbcDashboard.youthTable.column.status" />
                ),
                dataIndex: 'youth.status',
                render: (_value, record) => {
                  const entry = record.youth.meritBadgesCounseling.find(
                    m => m.meritBadgeId === record.mb.meritBadgeId,
                  );
                  return entry ? (
                    <AdvancementStatusTag status={entry.status} />
                  ) : (
                    <Text className={styles.text}>—</Text>
                  );
                },
              },
              {
                title: (
                  <FormattedMessage id="mbcDashboard.youthTable.column.version" />
                ),
                dataIndex: 'youth.version',
                render: (_value, record) => {
                  const entry = record.youth.meritBadgesCounseling.find(
                    m => m.meritBadgeId === record.mb.meritBadgeId,
                  );
                  return (
                    <Text className={styles.text}>{entry?.version ?? '—'}</Text>
                  );
                },
              },
              {
                title: '',
                width: 40,
                render(_value, record) {
                  const youthName = getPreferredName(record.youth);
                  const youth = record.youth;
                  const direction = isMobile ? 'vertical' : 'horizontal';
                  // TODO: we might be able to send mbIds
                  // const mbId = record.mb.meritBadgeId;
                  return (
                    <Space direction={direction}>
                      <Button
                        icon={<MBImage height={16} src={mbImage} width={16} />}
                        onClick={() => {
                          dispatch(
                            goToYouthAdvRequirements({
                              advancementId: record.mb.meritBadgeId,
                              advancementType: advancementTypes.MERIT_BADGES,
                              userId: youth.userId,
                            }),
                          );
                          dispatch(mbcDashboardActions.setNavigatedToMb(true));
                        }}
                        size="small"
                        type="default"
                      >
                        <FormattedMessage id="mbcDashboard.mbTable.viewMB" />
                      </Button>
                      <Tooltip title="Remove">
                        <Popconfirm
                          title={
                            <FormattedMessage id="mbcDashboard.mbTable.removeConfirm.title" />
                          }
                          description={
                            <FormattedMessage
                              values={{ youthName }}
                              id="mbcDashboard.mbTable.removeConfirm"
                            />
                          }
                          onConfirm={async () => {
                            if (!counselorMemberId) return;

                            try {
                              await removeCounselor({
                                body: {
                                  counselorMemberId,
                                  youths: [
                                    {
                                      meritBadges: [+mbId],
                                      youthMemberId: youth.memberId,
                                    },
                                  ],
                                },
                                extraTags: [
                                  tagFactory.MBCDashboard.userDashboard(
                                    mbcPersonGuid!,
                                  ),
                                ],
                              }).unwrap();
                              toastService.success(
                                <FormattedMessage id="mbcDashboard.actions.removeYouth.success" />,
                              );
                            } catch (error) {
                              const errorMessage = _.get(error, [
                                'data',
                                'errorDesc',
                              ]);
                              if (errorMessage) {
                                toastService.error(errorMessage);
                              }
                              return;
                            }
                          }}
                          okButtonProps={{ danger: true }}
                          cancelButtonProps={{
                            type: 'default',
                            disabled: isRemoving,
                          }}
                          onCancel={() => null}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            danger
                            icon={<MdDeleteOutline />}
                            onClick={props.onRemove}
                            size="small"
                            type="default"
                          >
                            <FormattedMessage id="shared.unassign" />
                          </Button>
                        </Popconfirm>
                      </Tooltip>
                    </Space>
                  );
                },
              },
            ]}
          />
        ),
      };
    })
    .sortBy('title')
    .value();

  return (
    <>
      <RemoveMBModal />
      <Collapse
        defaultActiveKey={items[0]?.key}
        className={styles.mbCollapse}
        items={items}
      />
    </>
  );
}
